import {Action, Card} from 'platform/components';
import {
  Box,
  Show,
  useElementSize,
  useResponsivePropValue,
  ValueByDevice,
  VStack,
} from 'platform/foundation';

import {any, isNil, length, slice} from 'ramda';
import {isNonEmptyArray} from 'ramda-adjunct';

import {
  ListSentToastNotificationsResponseBody,
  SentToastNotificationResponseBody,
  usePatchUnreadSentToastNotificationsMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {NotificationsWidget, useNotifications} from '@dms/notifications';
import {getVisibleRowCount, handleApiError} from '@dms/shared';

import {buildArray} from 'shared/web';

import {DashboardTileSize} from '../types/DashboardTileSize';
import {DashboardStretchRegion} from './DashboardStretchRegion';

const NOTIFICATION_FETCH_SIZE = 12;
const DEFAULT_NOTIFICATION_COLUMNS = 1;
const DEFAULT_NOTIFICATION_NATURAL_COUNT = 6;

interface MyNotificationsProps {
  size: DashboardTileSize;
}

interface MyNotificationsDensity {
  notificationColumns: ValueByDevice<number>;
  naturalNotificationCount: ValueByDevice<number>;
}

// A half tile has the room of two quarters, so it lists the notifications in two row-major columns.
const HALF_DENSITY: MyNotificationsDensity = {
  notificationColumns: [1, 2, 2, 2],
  naturalNotificationCount: [6, 12, 12, 12],
};

const isUnread = (notification: SentToastNotificationResponseBody) => !notification.isRead;

// Notifications vary in height, so a row is measured rather than assumed.
const getRowHeight = (naturalBlockHeight: number | null, naturalRowCount: number) =>
  isNil(naturalBlockHeight) || naturalRowCount === 0 ? null : naturalBlockHeight / naturalRowCount;

const withNotifications = (
  notificationData: ListSentToastNotificationsResponseBody | undefined,
  notifications: SentToastNotificationResponseBody[]
) => (isNil(notificationData) ? undefined : {...notificationData, notifications});

export function MyNotifications(props: MyNotificationsProps) {
  const {
    data: notificationData,
    isLoading,
    isFetching,
    isError,
  } = useNotifications(NOTIFICATION_FETCH_SIZE);

  const density = props.size === 'half' ? HALF_DENSITY : undefined;
  const notificationColumns = useResponsivePropValue(
    density?.notificationColumns ?? DEFAULT_NOTIFICATION_COLUMNS
  );
  const naturalNotificationCount = useResponsivePropValue(
    density?.naturalNotificationCount ?? DEFAULT_NOTIFICATION_NATURAL_COUNT
  );

  const [regionRef, , regionHeight] = useElementSize<HTMLDivElement>();
  const [naturalRowsRef, , naturalRowsHeight] = useElementSize<HTMLDivElement>();

  const [patchAllUnreadNotifications, {isLoading: isPatching}] =
    usePatchUnreadSentToastNotificationsMutation();

  const notifications = notificationData?.notifications ?? [];
  const naturalNotifications = slice(0, naturalNotificationCount, notifications);
  const naturalNotificationRows = Math.ceil(length(naturalNotifications) / notificationColumns);

  const visibleRows = getVisibleRowCount({
    naturalRows: naturalNotificationRows,
    regionHeight,
    rowHeight: getRowHeight(naturalRowsHeight, naturalNotificationRows),
  });
  const visibleNotificationCount = visibleRows * notificationColumns;
  const stretchedNotifications = slice(
    naturalNotificationCount,
    visibleNotificationCount,
    notifications
  );

  const hasUnreadNotifications = any(isUnread, [
    ...naturalNotifications,
    ...stretchedNotifications,
  ]);

  const handleMarkAllAsRead = () => {
    patchAllUnreadNotifications({body: {isRead: true}})
      .unwrap()
      .catch(handleApiError);
  };

  return (
    <Card
      title={i18n.t('page.homepage.labels.myNotifications')}
      actions={buildArray<Action>().when(hasUnreadNotifications, {
        title: i18n.t('general.notifications.markAllAsRead'),
        onClick: handleMarkAllAsRead,
        variant: 'link',
        type: 'button',
        isDisabled: isPatching,
      })}
      hasSeparator={false}
      isFullHeight
    >
      <VStack height="100%" minHeight={0}>
        <Box ref={naturalRowsRef}>
          <NotificationsWidget
            data-testid="naturalNotifications"
            notificationData={withNotifications(notificationData, naturalNotifications)}
            isLoading={isLoading}
            isError={isError}
            isFetching={isFetching}
            isDisabled={isPatching}
            isHeaderHidden
            columns={notificationColumns}
          />
        </Box>
        <DashboardStretchRegion ref={regionRef}>
          <Show when={isNonEmptyArray(stretchedNotifications)}>
            <NotificationsWidget
              data-testid="stretchedNotifications"
              notificationData={withNotifications(notificationData, stretchedNotifications)}
              isLoading={false}
              isError={false}
              isFetching={isFetching}
              isDisabled={isPatching}
              isHeaderHidden
              columns={notificationColumns}
            />
          </Show>
        </DashboardStretchRegion>
      </VStack>
    </Card>
  );
}
